/**
 * Locale PrimeVue minimi per IT/EN/FR/DE (DataTable, Calendar/DatePicker, FileUpload, ecc.).
 *
 * PrimeVue 4 non spedisce i locale per lingua: vanno forniti a mano (il pacchetto `primelocale`
 * NON è installato). Qui copriamo le stringhe più visibili + nomi giorni/mesi. `firstDayOfWeek=1`
 * (lunedì) e `dateFormat='dd/mm/yy'` per tutte le lingue (i formati restano stile europeo).
 *
 * Usati da `useLocale.setLocale()` per aggiornare `primevue.config.locale` al cambio lingua.
 */
export type PrimeVueLocale = Record<string, unknown>

const it: PrimeVueLocale = {
  accept: 'Sì',
  reject: 'No',
  choose: 'Scegli',
  upload: 'Carica',
  cancel: 'Annulla',
  clear: 'Pulisci',
  apply: 'Applica',
  dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
  dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
  dayNamesMin: ['D', 'L', 'M', 'M', 'G', 'V', 'S'],
  monthNames: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
  monthNamesShort: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
  today: 'Oggi',
  weekHeader: 'Sett',
  firstDayOfWeek: 1,
  dateFormat: 'dd/mm/yy',
  emptyMessage: 'Nessun risultato',
  emptyFilterMessage: 'Nessun risultato trovato',
  emptySelectionMessage: 'Nessun elemento selezionato',
}

const en: PrimeVueLocale = {
  accept: 'Yes',
  reject: 'No',
  choose: 'Choose',
  upload: 'Upload',
  cancel: 'Cancel',
  clear: 'Clear',
  apply: 'Apply',
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  today: 'Today',
  weekHeader: 'Wk',
  firstDayOfWeek: 1,
  dateFormat: 'dd/mm/yy',
  emptyMessage: 'No results found',
  emptyFilterMessage: 'No results found',
  emptySelectionMessage: 'No selected item',
}

const fr: PrimeVueLocale = {
  accept: 'Oui',
  reject: 'Non',
  choose: 'Choisir',
  upload: 'Téléverser',
  cancel: 'Annuler',
  clear: 'Effacer',
  apply: 'Appliquer',
  dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
  dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
  dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
  monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
  monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jui', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
  today: "Aujourd'hui",
  weekHeader: 'Sem',
  firstDayOfWeek: 1,
  dateFormat: 'dd/mm/yy',
  emptyMessage: 'Aucun résultat',
  emptyFilterMessage: 'Aucun résultat trouvé',
  emptySelectionMessage: 'Aucun élément sélectionné',
}

const de: PrimeVueLocale = {
  accept: 'Ja',
  reject: 'Nein',
  choose: 'Auswählen',
  upload: 'Hochladen',
  cancel: 'Abbrechen',
  clear: 'Zurücksetzen',
  apply: 'Anwenden',
  dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  dayNamesMin: ['S', 'M', 'D', 'M', 'D', 'F', 'S'],
  monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
  today: 'Heute',
  weekHeader: 'KW',
  firstDayOfWeek: 1,
  dateFormat: 'dd/mm/yy',
  emptyMessage: 'Keine Ergebnisse',
  emptyFilterMessage: 'Keine Ergebnisse gefunden',
  emptySelectionMessage: 'Kein Element ausgewählt',
}

export const PRIMEVUE_LOCALES: Record<string, PrimeVueLocale> = { it, en, fr, de }
